import * as React from 'react'
import { cn } from '@/lib/utils'

// ─── Context ──────────────────────────────────────────────────────────────────

interface DropdownCtx {
  open: boolean
  setOpen: (v: boolean) => void
}
const DropdownCtx = React.createContext<DropdownCtx>({ open: false, setOpen: () => {} })

// ─── Root ─────────────────────────────────────────────────────────────────────

interface DropdownMenuProps {
  children: React.ReactNode
}

function DropdownMenu({ children }: DropdownMenuProps) {
  const [open, setOpen] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <DropdownCtx.Provider value={{ open, setOpen }}>
      <div ref={ref} className="relative inline-block">
        {children}
      </div>
    </DropdownCtx.Provider>
  )
}

// ─── Trigger ─────────────────────────────────────────────────────────────────

function DropdownMenuTrigger({ children, asChild }: { children: React.ReactNode; asChild?: boolean }) {
  const { open, setOpen } = React.useContext(DropdownCtx)
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<{ onClick?: () => void }>, {
      onClick: () => setOpen(!open),
    })
  }
  return (
    <button type="button" onClick={() => setOpen(!open)}>
      {children}
    </button>
  )
}

// ─── Content ─────────────────────────────────────────────────────────────────

interface DropdownMenuContentProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: 'start' | 'end' | 'center'
  sideOffset?: number
}

function DropdownMenuContent({
  children,
  className,
  align = 'end',
  sideOffset: _sideOffset = 4,
  ...props
}: DropdownMenuContentProps) {
  const { open } = React.useContext(DropdownCtx)
  if (!open) return null

  const alignClasses = { start: 'left-0', center: 'left-1/2 -translate-x-1/2', end: 'right-0' }

  return (
    <div
      className={cn(
        'absolute top-full z-50 mt-1 min-w-[200px] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg',
        alignClasses[align],
        className,
      )}
      {...props}
    >
      <div className="p-1">{children}</div>
    </div>
  )
}

// ─── Items ────────────────────────────────────────────────────────────────────

function DropdownMenuItem({
  children,
  className,
  onClick,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const { setOpen } = React.useContext(DropdownCtx)
  return (
    <div
      role="menuitem"
      tabIndex={0}
      className={cn(
        'flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
        'hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
        className,
      )}
      onClick={() => {
        onClick?.(undefined as unknown as React.MouseEvent<HTMLDivElement>)
        setOpen(false)
      }}
      {...props}
    >
      {children}
    </div>
  )
}

function DropdownMenuLabel({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('px-2 py-1.5 text-xs font-semibold text-muted-foreground', className)}
      {...props}
    >
      {children}
    </div>
  )
}

function DropdownMenuSeparator({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('-mx-1 my-1 h-px bg-border', className)} {...props} />
}

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
}
